/**
 * Composable for managing real-time connections using SSE or WebSocket
 *
 * Provides a unified interface for real-time data updates with automatic
 * protocol selection, fallback support, and Vue reactivity.
 */

import { ref, computed, onUnmounted, readonly, type Ref } from 'vue'
import { SSEService, createSSEService, type SSEOptions, type SSEMessage, type SSEConnectionState } from '@/services/sse'
import { WebSocketService, createWebSocketService, type WebSocketOptions, type WebSocketMessage, type WebSocketConnectionState } from '@/services/websocket'

export type ConnectionState = SSEConnectionState | WebSocketConnectionState
export type RealtimeProtocol = 'sse' | 'websocket' | 'auto'

export interface PollingConfig {
  /** Default polling interval in ms (default: 30000) */
  defaultInterval: number
  /** Polling interval during live events in ms (default: 5000) */
  liveEventInterval: number
}

export interface RealtimeOptions {
  /** Protocol to use: 'sse', 'websocket', or 'auto' (default: 'auto') */
  protocol?: RealtimeProtocol
  /** SSE-specific options */
  sseOptions?: SSEOptions
  /** WebSocket-specific options */
  wsOptions?: WebSocketOptions
  /** Whether to connect immediately (default: false) */
  autoConnect?: boolean
  /** Polling fallback interval in ms (default: 30000, 0 to disable) */
  pollingFallback?: number
  /** Polling configuration for different modes */
  pollingConfig?: Partial<PollingConfig>
}

export interface RealtimeMessage<T = unknown> {
  type: string
  data: T
  timestamp?: number
  source: 'sse' | 'websocket' | 'polling'
}

type MessageHandler<T = unknown> = (message: RealtimeMessage<T>) => void

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

/** Default polling configuration */
const DEFAULT_POLLING_CONFIG: PollingConfig = {
  defaultInterval: 30000,  // 30 seconds for normal operation
  liveEventInterval: 5000  // 5 seconds during live events
}

/**
 * Global connection manager singleton for shared real-time connections
 */
class RealtimeConnectionManager {
  private sseService: SSEService | null = null
  private wsService: WebSocketService | null = null
  private pollingIntervals: Map<string, ReturnType<typeof setInterval>> = new Map()
  private messageHandlers: Map<string, Set<MessageHandler>> = new Map()
  private stateHandlers: Set<(state: ConnectionState) => void> = new Set()
  private _state: Ref<ConnectionState> = ref('disconnected')
  private _protocol: Ref<RealtimeProtocol> = ref('auto')
  private _activeProtocol: Ref<'sse' | 'websocket' | 'polling' | null> = ref(null)
  private _isLiveEventMode: Ref<boolean> = ref(false)
  private pollingConfig: PollingConfig = { ...DEFAULT_POLLING_CONFIG }
  private pollingFallbackInterval = 30000

  get state(): Readonly<Ref<ConnectionState>> {
    return readonly(this._state)
  }

  get protocol(): Readonly<Ref<RealtimeProtocol>> {
    return readonly(this._protocol)
  }

  get activeProtocol(): Readonly<Ref<'sse' | 'websocket' | 'polling' | null>> {
    return readonly(this._activeProtocol)
  }

  get isLiveEventMode(): Readonly<Ref<boolean>> {
    return readonly(this._isLiveEventMode)
  }

  /**
   * Connect using the specified protocol
   */
  connect(options: RealtimeOptions = {}): void {
    const { protocol = 'auto', sseOptions, wsOptions, pollingFallback = 30000, pollingConfig } = options
    this._protocol.value = protocol

    // Configure polling intervals
    if (pollingConfig) {
      this.pollingConfig = { ...DEFAULT_POLLING_CONFIG, ...pollingConfig }
    }
    this.pollingFallbackInterval = this._isLiveEventMode.value
      ? this.pollingConfig.liveEventInterval
      : (pollingFallback || this.pollingConfig.defaultInterval)

    if (protocol === 'sse' || protocol === 'auto') {
      this.connectSSE(sseOptions)
    } else if (protocol === 'websocket') {
      this.connectWebSocket(wsOptions)
    }
  }

  /**
   * Connect using SSE
   */
  private connectSSE(options?: SSEOptions): void {
    if (this.sseService) {
      this.sseService.disconnect()
    }

    const sseUrl = `${API_BASE_URL}/realtime/sse`
    this.sseService = createSSEService(sseUrl, {
      maxReconnectAttempts: 3,
      ...options
    })

    this.sseService.onStateChange((state) => {
      if (state === 'connected') {
        this._activeProtocol.value = 'sse'
        this._state.value = state
        this.stopPolling()
      } else if (state === 'disconnected' || state === 'error') {
        // If SSE fails and we're in auto mode, try WebSocket or polling
        if (this._protocol.value === 'auto') {
          this.fallbackToWebSocket()
        } else {
          this._state.value = state
          if (this.pollingFallbackInterval > 0) {
            this.startPolling()
          }
        }
      } else {
        this._state.value = state
      }
      this.notifyStateHandlers(state)
    })

    // Register message handlers
    this.sseService.on('*', (message: SSEMessage) => {
      this.dispatchMessage({
        type: message.type,
        data: message.data,
        timestamp: message.timestamp,
        source: 'sse'
      })
    })

    this.sseService.connect()
  }

  /**
   * Connect using WebSocket
   */
  private connectWebSocket(options?: WebSocketOptions): void {
    if (this.wsService) {
      this.wsService.disconnect()
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${wsProtocol}//${window.location.host}${API_BASE_URL}/realtime/ws`
    this.wsService = createWebSocketService(wsUrl, {
      maxReconnectAttempts: 3,
      ...options
    })

    this.wsService.onStateChange((state) => {
      if (state === 'connected') {
        this._activeProtocol.value = 'websocket'
        this._state.value = state
        this.stopPolling()
      } else if (state === 'disconnected' || state === 'error') {
        this._state.value = state
        // Fall back to polling if WebSocket fails
        if (this.pollingFallbackInterval > 0) {
          this.startPolling()
        }
      } else {
        this._state.value = state
      }
      this.notifyStateHandlers(state)
    })

    // Register message handlers
    this.wsService.on('*', (message: WebSocketMessage) => {
      this.dispatchMessage({
        type: message.type,
        data: message.data,
        timestamp: message.timestamp,
        source: 'websocket'
      })
    })

    this.wsService.connect()
  }

  /**
   * Fall back to WebSocket when SSE fails
   */
  private fallbackToWebSocket(): void {
    this.connectWebSocket()
  }

  /**
   * Start polling as final fallback
   */
  private startPolling(): void {
    if (this.pollingIntervals.size > 0) return

    this._activeProtocol.value = 'polling'
    this._state.value = 'connected'

    // Poll fighters endpoint
    const fightersInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/fighters`)
        if (response.ok) {
          const data = await response.json()
          this.dispatchMessage({
            type: 'fighters_update',
            data,
            timestamp: Date.now(),
            source: 'polling'
          })
        }
      } catch {
        // Ignore polling errors
      }
    }, this.pollingFallbackInterval)
    this.pollingIntervals.set('fighters', fightersInterval)

    // Poll events endpoint
    const eventsInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/events`)
        if (response.ok) {
          const data = await response.json()
          this.dispatchMessage({
            type: 'events_update',
            data,
            timestamp: Date.now(),
            source: 'polling'
          })
        }
      } catch {
        // Ignore polling errors
      }
    }, this.pollingFallbackInterval)
    this.pollingIntervals.set('events', eventsInterval)
  }

  /**
   * Stop all polling intervals
   */
  private stopPolling(): void {
    this.pollingIntervals.forEach(interval => clearInterval(interval))
    this.pollingIntervals.clear()
  }

  /**
   * Dispatch message to registered handlers
   */
  private dispatchMessage(message: RealtimeMessage): void {
    // Dispatch to specific type handlers
    const typeHandlers = this.messageHandlers.get(message.type)
    typeHandlers?.forEach(handler => handler(message))

    // Dispatch to wildcard handlers
    const wildcardHandlers = this.messageHandlers.get('*')
    wildcardHandlers?.forEach(handler => handler(message))
  }

  /**
   * Notify state handlers
   */
  private notifyStateHandlers(state: ConnectionState): void {
    this.stateHandlers.forEach(handler => handler(state))
  }

  /**
   * Register a message handler
   */
  on<T = unknown>(type: string, handler: MessageHandler<T>): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set())
    }
    this.messageHandlers.get(type)!.add(handler as MessageHandler)

    return () => {
      this.messageHandlers.get(type)?.delete(handler as MessageHandler)
    }
  }

  /**
   * Register a state change handler
   */
  onStateChange(handler: (state: ConnectionState) => void): () => void {
    this.stateHandlers.add(handler)
    handler(this._state.value)

    return () => {
      this.stateHandlers.delete(handler)
    }
  }

  /**
   * Send a message (only works with WebSocket)
   */
  send<T>(message: { type: string; data: T }): boolean {
    if (this.wsService && this._activeProtocol.value === 'websocket') {
      return this.wsService.send(message)
    }
    return false
  }

  /**
   * Disconnect all connections
   */
  disconnect(): void {
    this.sseService?.disconnect()
    this.wsService?.disconnect()
    this.stopPolling()
    this._activeProtocol.value = null
    this._state.value = 'disconnected'
  }

  /**
   * Reconnect
   */
  reconnect(): void {
    this.disconnect()
    this.connect({ protocol: this._protocol.value })
  }

  /**
   * Set live event mode to adjust polling frequency
   * When enabled, polling uses liveEventInterval (5s default) for faster updates
   * When disabled, polling uses defaultInterval (30s default)
   */
  setLiveEventMode(enabled: boolean): void {
    if (this._isLiveEventMode.value === enabled) return

    this._isLiveEventMode.value = enabled
    this.pollingFallbackInterval = enabled
      ? this.pollingConfig.liveEventInterval
      : this.pollingConfig.defaultInterval

    // Restart polling with new interval if currently polling
    if (this._activeProtocol.value === 'polling') {
      this.stopPolling()
      this.startPolling()
    }
  }

  /**
   * Get current polling configuration
   */
  getPollingConfig(): PollingConfig {
    return { ...this.pollingConfig }
  }

  /**
   * Update polling configuration
   */
  setPollingConfig(config: Partial<PollingConfig>): void {
    this.pollingConfig = { ...this.pollingConfig, ...config }

    // Update current interval based on mode
    this.pollingFallbackInterval = this._isLiveEventMode.value
      ? this.pollingConfig.liveEventInterval
      : this.pollingConfig.defaultInterval

    // Restart polling with new interval if currently polling
    if (this._activeProtocol.value === 'polling') {
      this.stopPolling()
      this.startPolling()
    }
  }
}

// Global singleton instance
let connectionManager: RealtimeConnectionManager | null = null

function getConnectionManager(): RealtimeConnectionManager {
  if (!connectionManager) {
    connectionManager = new RealtimeConnectionManager()
  }
  return connectionManager
}

/**
 * Composable for using real-time connections in Vue components
 */
export function useRealtime(options: RealtimeOptions = {}) {
  const manager = getConnectionManager()
  const unsubscribers: (() => void)[] = []

  // Connect if autoConnect is enabled
  if (options.autoConnect) {
    manager.connect(options)
  }

  // Reactive state
  const state = manager.state
  const activeProtocol = manager.activeProtocol
  const isLiveEventMode = manager.isLiveEventMode
  const isConnected = computed(() => state.value === 'connected')
  const isConnecting = computed(() => state.value === 'connecting')
  const hasError = computed(() => state.value === 'error')

  /**
   * Subscribe to a message type
   */
  function subscribe<T = unknown>(type: string, handler: MessageHandler<T>): void {
    const unsubscribe = manager.on(type, handler)
    unsubscribers.push(unsubscribe)
  }

  /**
   * Subscribe to connection state changes
   */
  function onStateChange(handler: (state: ConnectionState) => void): void {
    const unsubscribe = manager.onStateChange(handler)
    unsubscribers.push(unsubscribe)
  }

  /**
   * Connect to real-time services
   */
  function connect(): void {
    manager.connect(options)
  }

  /**
   * Disconnect from real-time services
   */
  function disconnect(): void {
    manager.disconnect()
  }

  /**
   * Reconnect to real-time services
   */
  function reconnect(): void {
    manager.reconnect()
  }

  /**
   * Send a message (WebSocket only)
   */
  function send<T>(message: { type: string; data: T }): boolean {
    return manager.send(message)
  }

  /**
   * Enable or disable live event mode for faster polling
   */
  function setLiveEventMode(enabled: boolean): void {
    manager.setLiveEventMode(enabled)
  }

  /**
   * Get current polling configuration
   */
  function getPollingConfig(): PollingConfig {
    return manager.getPollingConfig()
  }

  /**
   * Update polling configuration
   */
  function setPollingConfig(config: Partial<PollingConfig>): void {
    manager.setPollingConfig(config)
  }

  // Cleanup on component unmount
  onUnmounted(() => {
    unsubscribers.forEach(fn => fn())
  })

  return {
    // State
    state,
    activeProtocol,
    isLiveEventMode,
    isConnected,
    isConnecting,
    hasError,
    // Methods
    connect,
    disconnect,
    reconnect,
    subscribe,
    onStateChange,
    send,
    setLiveEventMode,
    getPollingConfig,
    setPollingConfig
  }
}

/**
 * Get the global connection manager for use outside components
 */
export function getRealtimeManager(): RealtimeConnectionManager {
  return getConnectionManager()
}
